import { useState, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { agencyService } from '../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Building2, UserPlus, CheckCircle2 } from 'lucide-react';

function InvitePage() {
  const { agencyId } = useParams<{ agencyId: string }>();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'agent' | 'admin'>('agent');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!agencyId) return;
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await agencyService.invite(agencyId, email, role);
      setSuccess(`Invitación enviada a ${email} como ${role === 'agent' ? 'agente' : 'admin'}.`);
      setEmail('');
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'Error al enviar la invitación. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background">
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center gap-3">
          <button
            onClick={() => navigate('/properties')}
            aria-label="Volver"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-primary" />
            <span className="font-medium text-sm">Invitar miembro</span>
          </div>
        </div>
      </header>

      <main className="max-w-sm mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
            <UserPlus className="w-5 h-5 text-primary" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Invitar a la agencia</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Agrega un agente o admin a tu equipo
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="agente@inmobiliaria.com"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="role">Rol</Label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as 'agent' | 'admin')}
              className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring transition-shadow"
            >
              <option value="agent">Agente</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {success && (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              {success}
            </div>
          )}

          <Button
            type="submit"
            className="w-full transition-all active:scale-[0.98]"
            disabled={loading}
          >
            {loading ? 'Enviando...' : 'Enviar invitación'}
          </Button>
        </form>
      </main>
    </div>
  );
}

export default InvitePage;
